import { type ApiClient, createApiClient } from "@platform/contracts/client";

export type ClientKind = "web" | "desktop";

/** Where the desktop keeps its refresh token (the OS keychain); the web relies on the httpOnly cookie instead. */
export interface RefreshTokenStore {
  load(): Promise<string | undefined>;
  save(token: string): Promise<void>;
  clear(): Promise<void>;
}

export interface Tokens {
  readonly accessToken: string;
  readonly refreshToken?: string | undefined;
}

/** The claims the screens read; the API verifies the token, the client only decodes it. */
export interface SessionClaims {
  readonly sub: string;
  readonly ent?: string;
  /** The membership in the active enterprise, which is also the id of the person's business card there. */
  readonly mbr?: string;
  readonly perm: readonly string[];
  readonly mfaRequired?: boolean;
}

export type SessionState =
  | { readonly status: "loading" }
  /** `signedInElsewhereAt`: another sign-in of this kind took over then (ADR 0030), which the sign-in screen says. */
  | { readonly status: "signed_out"; readonly signedInElsewhereAt?: string }
  /** The desktop holds a refresh token but cannot reach the API; it may show what it keeps locally (IM-7). */
  | { readonly status: "offline" }
  | { readonly status: "signed_in"; readonly claims: SessionClaims };

export interface SessionOptions {
  readonly baseUrl: string;
  readonly client: ClientKind;
  /** `/v1/auth` for users (the default), `/v1/ops/auth` for operators in the admin console. */
  readonly authPath?: "/v1/auth" | "/v1/ops/auth";
  readonly refreshStore?: RefreshTokenStore | undefined;
  readonly fetch?: typeof globalThis.fetch;
}

export function decodeClaims(token: string): SessionClaims {
  const part = token.split(".")[1] ?? "";
  const json = atob(
    part
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(part.length / 4) * 4, "="),
  );
  const payload = JSON.parse(json) as Partial<SessionClaims>;
  return {
    sub: payload.sub ?? "",
    perm: payload.perm ?? [],
    ...(payload.ent === undefined ? {} : { ent: payload.ent }),
    ...(payload.mbr === undefined ? {} : { mbr: payload.mbr }),
    ...(payload.mfaRequired === undefined ? {} : { mfaRequired: payload.mfaRequired }),
  };
}

/**
 * The signed-in session (docs/05-api-contracts.md section 3): the access token lives in memory only; the refresh
 * token is an httpOnly cookie on the web and a keychain entry on the desktop. An expired access token is
 * refreshed once, transparently, and concurrent refreshes share one request so rotation never trips reuse detection.
 */
export class Session {
  readonly api: ApiClient;
  private accessToken: string | undefined;
  private state: SessionState = { status: "loading" };
  private readonly listeners = new Set<() => void>();
  private refreshing: Promise<boolean> | undefined;
  private readonly baseFetch: typeof globalThis.fetch;
  private readonly authPath: string;

  constructor(private readonly options: SessionOptions) {
    this.authPath = options.authPath ?? "/v1/auth";
    this.baseFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.api = createApiClient({
      baseUrl: options.baseUrl,
      getAccessToken: () => this.accessToken,
      fetch: (input) => this.fetchWithRefresh(input as Request),
    });
  }

  get client(): ClientKind {
    return this.options.client;
  }

  get snapshot(): SessionState {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Restores a session at start from the cookie or the keychain. A desktop that cannot reach the API keeps its
   * refresh token and is `offline` until a later `restore` gets through.
   */
  async restore(): Promise<void> {
    if (await this.refresh()) return;
    // A refused refresh has signed out already, keeping why (ADR 0030); an unreachable API left the desktop offline.
    if (this.state.status !== "offline" && this.state.status !== "signed_out")
      this.set({ status: "signed_out" });
  }

  /** Takes tokens from sign-in, a switch, or a refresh. */
  async accept(tokens: Tokens): Promise<void> {
    if (tokens.refreshToken !== undefined) await this.options.refreshStore?.save(tokens.refreshToken);
    this.takeAccessToken(tokens.accessToken);
  }

  /** Takes an access token alone, as step-up and TOTP enrolment return. */
  takeAccessToken(accessToken: string): void {
    this.accessToken = accessToken;
    this.set({ status: "signed_in", claims: decodeClaims(accessToken) });
  }

  /**
   * The access token, refreshed first when it expires within a minute (card 149), for a connection that presents it
   * itself, such as the desktop's to Centrifugo. Undefined without a session.
   */
  async freshAccessToken(): Promise<string | undefined> {
    if (this.accessToken === undefined || expiresWithin(this.accessToken, FRESH_FOR_MS)) await this.refresh();
    return this.accessToken;
  }

  refresh(): Promise<boolean> {
    this.refreshing ??= this.rotate(`${this.authPath}/refresh`, {}).finally(() => {
      this.refreshing = undefined;
    });
    return this.refreshing;
  }

  /** Makes another enterprise active; both tokens are rotated (IAM-2). */
  async switchEnterprise(enterpriseId: string): Promise<boolean> {
    return this.rotate("/v1/auth/switch-enterprise", { enterpriseId });
  }

  async signOut(): Promise<void> {
    await this.baseFetch(`${this.options.baseUrl}${this.authPath}/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "idempotency-key": crypto.randomUUID(),
        ...(this.accessToken === undefined ? {} : { authorization: `Bearer ${this.accessToken}` }),
      },
    }).catch(() => undefined);
    await this.options.refreshStore?.clear();
    this.accessToken = undefined;
    this.set({ status: "signed_out" });
  }

  private async rotate(path: string, body: Record<string, string>): Promise<boolean> {
    const refreshToken = await this.options.refreshStore?.load();
    if (this.options.client === "desktop" && refreshToken === undefined) return false;
    const headers: Record<string, string> = { "content-type": "application/json" };
    const isRefresh = path.endsWith("/refresh");
    if (!isRefresh && this.accessToken !== undefined) headers.authorization = `Bearer ${this.accessToken}`;
    if (!isRefresh) headers["idempotency-key"] = crypto.randomUUID();
    const response = await this.baseFetch(`${this.options.baseUrl}${path}`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ ...body, ...(refreshToken === undefined ? {} : { refreshToken }) }),
    }).catch(() => undefined);
    if (
      response === undefined &&
      isRefresh &&
      refreshToken !== undefined &&
      this.state.status !== "signed_in"
    ) {
      this.set({ status: "offline" });
      return false;
    }
    if (response?.ok !== true) {
      if (response?.status === 401) await this.signedOut(await signedInElsewhereAt(response));
      return false;
    }
    await this.accept((await response.json()) as Tokens);
    return true;
  }

  private async fetchWithRefresh(request: Request): Promise<Response> {
    const retry = request.clone();
    const response = await this.baseFetch(request);
    if (response.status !== 401 || request.url.includes(`${this.authPath}/`)) return response;
    const elsewhere = await signedInElsewhereAt(response);
    // Signed in elsewhere: this session is over at once, wherever the request was going (ADR 0030).
    if (elsewhere !== undefined) {
      await this.signedOut(elsewhere);
      return response;
    }
    const problem = (await response
      .clone()
      .json()
      .catch(() => ({}))) as { code?: string };
    // An expired token is refreshed once, and so is one the API no longer takes, as after its keys changed or it
    // restarted with throwaway ones locally (card 151). A session revoked meanwhile fails that refresh and signs out
    // at once, rather than when its token would have expired.
    const refreshable = problem.code === "TOKEN_EXPIRED" || problem.code === "AUTH_REQUIRED";
    if (!refreshable || !(await this.refresh())) return response;
    retry.headers.set("authorization", `Bearer ${this.accessToken}`);
    return this.baseFetch(retry);
  }

  private async signedOut(signedInElsewhereAt: string | undefined): Promise<void> {
    await this.options.refreshStore?.clear();
    this.accessToken = undefined;
    this.set(
      signedInElsewhereAt === undefined
        ? { status: "signed_out" }
        : { status: "signed_out", signedInElsewhereAt },
    );
  }

  private set(state: SessionState): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }
}

/** How long a token handed to a connection must still be good for. */
const FRESH_FOR_MS = 60_000;

/** Whether a token expires within `ms`, by its `exp`; one that cannot be read counts as expiring. */
function expiresWithin(token: string, ms: number): boolean {
  try {
    const part = token.split(".")[1] ?? "";
    const payload = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: unknown };
    return typeof payload.exp !== "number" || payload.exp * 1000 - Date.now() < ms;
  } catch {
    return true;
  }
}

/** When another sign-in of this kind took over, from a refusal that says so (ADR 0030); otherwise undefined. */
async function signedInElsewhereAt(response: Response): Promise<string | undefined> {
  const problem = (await response
    .clone()
    .json()
    .catch(() => ({}))) as { code?: string; signedInAt?: string };
  return problem.code === "SIGNED_IN_ELSEWHERE"
    ? (problem.signedInAt ?? new Date().toISOString())
    : undefined;
}
